import React, { useState } from 'react';
import styled from 'styled-components';
import { useCart } from 'contexts/CartContext';
import { createCart, getVariantBySize } from 'lib/shopify';
import logosmix from 'assets/logosmix.png';
import recycleSound from 'assets/windows_xp_sounds/Windows XP Recycle.wav';

function Cart({ onClose, onShowDialog, onOpenProduct }) {
  const { items, removeItem, updateQuantity, clearCart, getTotal, getItemCount } = useCart();
  const [checkingOut, setCheckingOut] = useState(false);

  async function handleCheckout() {
    setCheckingOut(true);
    try {
      const lines = items.map(item => {
        const variantId = item.product.variants
          ? getVariantBySize(item.product, item.size, item.color)
          : null;
        return {
          variantId,
          quantity: item.quantity,
          name: item.product.name,
        };
      });
      const invalid = lines.find(l => !l.variantId);
      if (invalid) {
        onShowDialog && onShowDialog('error', `"${invalid.name}" variant is unavailable. Please update your cart.`);
        setCheckingOut(false);
        return;
      }

      const cart = await createCart(lines.map(l => ({ variantId: l.variantId, quantity: l.quantity })));
      window.open(cart.checkoutUrl, '_blank');
    } catch (err) {
      onShowDialog && onShowDialog('error', `Checkout failed: ${err.message}`);
    }
    setCheckingOut(false);
  }

  return (
    <CartDiv>
      <div className="cart-header">
        <span className="cart-header__title">Shopping Cart</span>
        <span className="cart-header__count">{getItemCount()} item{getItemCount() !== 1 ? 's' : ''}</span>
      </div>

      <div className="cart-body">
        {items.length === 0 ? (
          <div className="cart-empty">
            <p>Your cart is empty.</p>
            <p>Browse Binco and add some items!</p>
          </div>
        ) : (
          <div className="cart-items">
            {items.map((item, i) => (
              <div key={`${item.product.id}-${item.size}-${item.color}`} className="cart-item">
                <div className="cart-item__image" style={{ cursor: 'pointer' }} onClick={() => onOpenProduct && onOpenProduct(item.product, { color: item.color, size: item.size, fromCart: true })}>
                  <img src={
                    (item.color && item.product.colorImages && item.product.colorImages[item.color]?.[0])
                    || item.product.images[0]
                  } alt={item.product.name} />
                </div>
                <div className="cart-item__details">
                  <div className="cart-item__name" style={{ cursor: 'pointer' }} onClick={() => onOpenProduct && onOpenProduct(item.product, { color: item.color, size: item.size, fromCart: true })}>{item.product.name}</div>
                  <div className="cart-item__meta">
                    {item.color && <span className="cart-item__color">{item.color} /</span>}
                    <span className="cart-item__size">Size: {item.size}</span>
                    <span className="cart-item__category">{item.product.category}</span>
                  </div>
                  <div className="cart-item__price-each">₹{item.product.price.toLocaleString()} each</div>
                </div>
                <div className="cart-item__qty">
                  <button
                    className="cart-item__qty-btn"
                    onClick={() => updateQuantity(item.product.id, item.size, item.color, item.quantity + 1)}
                  >
                    +
                  </button>
                  <span className="cart-item__qty-val">{item.quantity}</span>
                  <button
                    className="cart-item__qty-btn"
                    onClick={() => { updateQuantity(item.product.id, item.size, item.color, item.quantity - 1); if (item.quantity <= 1) { try { new Audio(recycleSound).play().catch(() => {}); } catch(e) {} } }}
                  >
                    −
                  </button>
                </div>
                <div className="cart-item__subtotal">
                  ₹{(item.product.price * item.quantity).toLocaleString()}
                </div>
                <button
                  className="cart-item__remove"
                  onClick={() => { removeItem(item.product.id, item.size, item.color); try { new Audio(recycleSound).play().catch(() => {}); } catch(e) {} }}
                  title="Remove"
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {items.length > 0 && (
        <div className="cart-footer">
          <div className="cart-footer__summary">
            <div className="cart-footer__row">
              <span>Subtotal ({getItemCount()} items)</span>
              <span>₹{getTotal().toLocaleString()}</span>
            </div>
            <div className="cart-footer__row">
              <span>Shipping</span>
              <span>{getTotal() >= 999 ? 'FREE' : '₹99'}</span>
            </div>
            <div className="cart-footer__row cart-footer__row--total">
              <span>Total</span>
              <span>₹{(getTotal() + (getTotal() >= 999 ? 0 : 99)).toLocaleString()}</span>
            </div>
          </div>
          <div className="cart-footer__actions">
            <button className="cart-footer__clear" onClick={() => {
              onShowDialog && onShowDialog('success', 'Cart cleared.', {
                buttons: [{ label: 'OK' }],
              });
              clearCart();
              try { new Audio(recycleSound).play().catch(() => {}); } catch(e) {}
            }}>
              Clear Cart
            </button>
            <button
              className="cart-footer__checkout"
              onClick={handleCheckout}
              disabled={checkingOut}
            >
              {checkingOut ? 'Loading...' : 'Proceed to Safe Checkout'}
            </button>
          </div>
        </div>
      )}
      <div className="cart-badges">
        <img src={logosmix} alt="Powered by Shopify & Razorpay" />
      </div>
    </CartDiv>
  );
}

const CartDiv = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
  display: flex;
  flex-direction: column;
  background: #1a1a1a;
  color: #c0c0c0;
  font-family: Tahoma, Verdana, sans-serif;
  font-size: 11px;
  overflow: hidden;

  .cart-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 14px;
    background: linear-gradient(180deg, #2d2d2d 0%, #1a1a1a 100%);
    border-bottom: 2px solid #f5a623;
  }
  .cart-header__title {
    font-size: 14px;
    font-weight: bold;
    color: #e0e0e0;
  }
  .cart-header__count {
    color: #f5a623;
    font-size: 11px;
    font-weight: bold;
  }

  .cart-body {
    flex: 1;
    overflow-y: auto;
  }

  .cart-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    color: #555;
    font-size: 12px;
    gap: 4px;
    p { margin: 0; }
  }

  .cart-items {
    display: flex;
    flex-direction: column;
  }

  .cart-item {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 8px;
    &:hover {
      background: rgba(245, 166, 35, 0.03);
    }
  }
  .cart-item__image {
    width: 60px;
    height: 75px;
    flex-shrink: 0;
    background: #111;
    border: 1px solid #333;
    overflow: hidden;
    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }
  .cart-item__details {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 1px;
    overflow: hidden;
  }
  .cart-item__name {
    font-size: 12px;
    font-weight: bold;
    color: #ddd;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .cart-item__meta {
    display: flex;
    gap: 6px;
    font-size: 10px;
    color: #888;
    flex-wrap: wrap;
  }
  .cart-item__category {
    text-transform: uppercase;
    font-size: 9px;
    color: #666;
  }
  .cart-item__price-each {
    font-size: 10px;
    color: #999;
  }

  .cart-item__qty {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2px;
    flex-shrink: 0;
  }
  .cart-item__qty-btn {
    width: 20px;
    height: 18px;
    background: #222;
    border: 1px solid #444;
    color: #ccc;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    padding: 0;
    &:hover {
      background: #333;
      color: #f5a623;
      border-color: #f5a623;
    }
  }
  .cart-item__qty-val {
    width: 22px;
    text-align: center;
    font-weight: bold;
    color: #e0e0e0;
    font-size: 12px;
  }

  .cart-item__subtotal {
    font-weight: bold;
    color: #f5a623;
    font-size: 13px;
    min-width: 50px;
    text-align: right;
    flex-shrink: 0;
  }

  .cart-item__remove {
    background: none;
    border: none;
    color: #666;
    cursor: pointer;
    font-size: 12px;
    padding: 4px;
    flex-shrink: 0;
    &:hover {
      color: #ff4444;
    }
  }

  .cart-footer {
    border-top: 1px solid #333;
    background: #1e1e1e;
    padding: 10px 14px;
  }
  .cart-footer__summary {
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin-bottom: 10px;
  }
  .cart-footer__row {
    display: flex;
    justify-content: space-between;
    font-size: 11px;
    color: #999;
    &--total {
      font-size: 14px;
      font-weight: bold;
      color: #e0e0e0;
      padding-top: 6px;
      border-top: 1px solid #333;
      margin-top: 4px;
    }
  }
  .cart-footer__actions {
    display: flex;
    gap: 8px;
  }
  .cart-footer__clear {
    background: #222;
    border: 1px solid #444;
    color: #999;
    padding: 6px 14px;
    cursor: pointer;
    font-size: 11px;
    font-family: Tahoma, sans-serif;
    &:hover {
      background: #333;
      color: #ff4444;
      border-color: #ff4444;
    }
  }
  .cart-footer__checkout {
    flex: 1;
    background: linear-gradient(180deg, #f5a623 0%, #d4891a 100%);
    border: 1px solid #f5a623;
    color: #000;
    padding: 6px 14px;
    cursor: pointer;
    font-weight: bold;
    font-size: 12px;
    font-family: Tahoma, sans-serif;
    &:hover {
      background: linear-gradient(180deg, #ffb833 0%, #e09520 100%);
    }
  }

  .cart-badges {
    display: flex;
    justify-content: center;
    padding: 6px 0;
    opacity: 0.12;
    pointer-events: none;
    margin-top: auto;
    img {
      height: 18px;
      width: auto;
      filter: invert(1) brightness(2);
    }
  }
`;

export default Cart;
